// @ts-nocheck
import React, { Suspense, useState, useEffect, useRef } from 'react';
import { ArrowLeft, BookOpen, Briefcase, Building2, Calendar, Check, ChevronDown, ChevronRight, DollarSign, Globe, Menu, Moon, Package, Sun, User, X, Zap } from 'lucide-react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { SHOW_PRELAUNCH_NAV_ITEMS, WAITLIST_MODE } from '../constants/featureFlags';
import i18n, { persistLanguage, SUPPORTED } from '../lib/i18n.ts';
import { useCurrency, CURRENCIES } from '../lib/currencyContext';
import { isDesktopRuntime } from '../lib/desktop-bridge.ts';
import { PRODUCTION_PRODUCT_MODE } from '../lib/product-mode.ts';
import './HeaderStyles.ts';

const HeaderAuthActions = React.lazy(() => import('./HeaderAuthActions'));

const LANGUAGE_OPTIONS = SUPPORTED.map((code) => ({ code, key: `languages.${code}` }));
export default function Header({ theme, toggleTheme, onOpenAuth }) {
  const { t, i18n: headerI18n } = useTranslation();
  const observatoryName = String(headerI18n?.language || headerI18n?.resolvedLanguage || 'en').startsWith('fr') ? 'Observatoire' : 'Observatory';
  const [isScrolled, setIsScrolled] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileView, setMobileView] = useState('explore');
  const [mobileLanguageOpen, setMobileLanguageOpen] = useState(false);
  const jobsTriggerRef = useRef(null);
  const salariesTriggerRef = useRef(null);
  const burgerBtnRef = useRef(null);
  const mobileDrawerRef = useRef(null);
  const prelaunchMode = !SHOW_PRELAUNCH_NAV_ITEMS;
  const waitlistMode = WAITLIST_MODE;
  const location = useLocation();
  const observatoryBrand = /^\/observatory(?:\/|$)/.test(location.pathname);
  const logoSrc = observatoryBrand ? `/logo-observatory-${theme === 'dark' ? 'dark' : 'light'}.svg` : theme === 'dark' ? '/logo-aic-dark.webp' : '/logo-aic-light.webp';
  const brandTarget = '/';
  const navigate = useNavigate();
  const currentLanguage = (i18n.resolvedLanguage || i18n.language || 'en').slice(0, 2).toLowerCase();
  const desktopRuntime = isDesktopRuntime();

  // Close mobile menu on route change
  useEffect(() => {
    setMobileMenuOpen(false);
    setMobileView('explore');
    setMobileLanguageOpen(false);
  }, [location.pathname]);

  // Prevent body scroll when drawer is open; slide header/bottom-bar away
  useEffect(() => {
    document.body.style.overflow = mobileMenuOpen ? 'hidden' : '';
    document.body.classList.toggle('mobile-menu-open', mobileMenuOpen);
    return () => {
      document.body.style.overflow = '';
      document.body.classList.remove('mobile-menu-open');
    };
  }, [mobileMenuOpen]);

  useEffect(() => {
    if (mobileMenuOpen) {
      const drawer = mobileDrawerRef.current;
      if (drawer) {
        const focusable = drawer.querySelectorAll(
          'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])'
        );
        focusable[0]?.focus();
      }
    } else {
      burgerBtnRef.current?.focus();
    }
  }, [mobileMenuOpen]);

  useEffect(() => {
    if (mobileMenuOpen && mobileView !== 'explore') {
      mobileDrawerRef.current?.querySelector('.mobile-drawer-back')?.focus();
    }
  }, [mobileMenuOpen, mobileView]);

  const handleDrawerKeyDown = (e) => {
    if (!mobileMenuOpen) return;
    if (e.key === 'Escape') {
      setMobileMenuOpen(false);
      setMobileView('explore');
      setMobileLanguageOpen(false);
      return;
    }
    if (e.key !== 'Tab') return;
    const drawer = mobileDrawerRef.current;
    if (!drawer) return;
    const focusable = Array.from(
      drawer.querySelectorAll('a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])')
    );
    if (!focusable.length) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (e.shiftKey) {
      if (document.activeElement === first) {
        e.preventDefault();
        last.focus();
      }
    } else if (document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  };

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    handleScroll();
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleBrandClick = (e) => {
    if (typeof window === 'undefined') return;
    if (location.pathname === '/') {
      e.preventDefault();
      window.location.assign('/');
    }
  };

  const close = () => {
    setMobileMenuOpen(false);
    setMobileView('explore');
    setMobileLanguageOpen(false);
  };
  const openMobileView = (view) => {
    setMobileLanguageOpen(false);
    setMobileView(view);
  };
  const changeLanguage = async (code) => {
    await i18n.changeLanguage(code);
    persistLanguage(code);
    if (/^\/learn-ai\/article\/[^/]+$/.test(location.pathname)) {
      const params = new URLSearchParams(location.search);
      params.set('lang', code);
      navigate({ pathname: location.pathname, search: `?${params.toString()}`, hash: location.hash });
    }
    setOpenDropdown(null);
    setMobileLanguageOpen(false);
  };

  const { currency: currentCurrency, setCurrency } = useCurrency();
  const mobileViews = {
    jobs: {
      title: t('header.jobs'),
      description: t('mobileNav.jobsDescription'),
      items: [
        { to: '/', label: t('header.allAiJobs') },
        { to: '/top-ai-internships', label: t('header.topAiInternships') },
        { to: '/ai-internships', label: t('header.aiInternships') },
        { to: '/entry-developer-jobs', label: t('header.developerJobs') },
        { to: '/entry-non-tech-jobs', label: t('header.nonTechJobs') },
        { to: '/entry-designer-jobs', label: t('header.designerJobs') },
        { to: '/?remote=true', label: t('header.remoteJobs') },
      ],
    },
    salaries: {
      title: t('header.salaries'),
      description: t('mobileNav.salariesDescription'),
      items: [
        { to: '/salaries', label: t('header.globalOverview') },
        { to: '/observatory', label: observatoryName },
        { to: '/developer-salaries', label: t('header.developerSalaries') },
        { to: '/non-tech-salaries', label: t('header.nonTechSalaries') },
        { to: '/highest-paying-ai-jobs', label: t('header.highestPayingAiJobs') },
        { to: '/usa-vs-world-salaries', label: t('header.usaVsWorldSalaries') },
      ],
    },
    products: PRODUCTION_PRODUCT_MODE ? null : {
      title: t('mobileNav.products'),
      description: t('mobileNav.productsDescription'),
      items: [
        { to: '/account/cv', label: t('mobileNav.cvBuilder') },
        { to: '/account/agent', label: t('mobileNav.careerAgent') },
        { to: '/account/swipe', label: t('mobileNav.swipeToWork') },
        { to: '/desktop/workspace', label: t('mobileNav.keshaApp') },
      ],
    },
  };
  const activeMobileView = mobileViews[mobileView];

  return (
    <>
      <header className={`header ${isScrolled ? 'scrolled' : ''}`}>
        <div className={`container header-container ${prelaunchMode ? 'header-container-prelaunch' : ''}`}>
          <div className={`header-left ${prelaunchMode ? 'header-left-prelaunch' : ''}`}>
            <Link to={brandTarget} className={`brand${observatoryBrand ? ' brand-observatory' : ''}`} aria-label={observatoryBrand ? `AI Careers ${observatoryName}` : undefined} discover="none" onClick={handleBrandClick}>
              <img
                src={logoSrc}
                alt={observatoryBrand ? '' : 'AI Careers logo'}
                className="brand-logo-image"
                width="40"
                height="40"
                decoding="async"
                fetchPriority="high"
              />
              <span className="brand-text">AI Careers{observatoryBrand && <small>{observatoryName}</small>}</span>
            </Link>

            <nav className={`nav-menu ${prelaunchMode ? 'nav-menu-prelaunch' : ''}`}>
              <ul className="nav-list">
                {waitlistMode ? (
                  <>
                    <li><Link to="/learn-ai" discover="none">{t('header.learnAi')}</Link></li>
                    <li><Link to="/about" discover="none">{t('footer.aboutUs')}</Link></li>
                  </>
                ) : (
                  <>
                    <li
                      className={`dropdown${openDropdown === 'jobs' ? ' is-open' : ''}`}
                      onMouseEnter={() => setOpenDropdown('jobs')}
                      onMouseLeave={() => setOpenDropdown(null)}
                    >
                      <Link
                        to="/"
                        className="dropdown-trigger"
                        ref={jobsTriggerRef}
                        aria-haspopup="true"
                        aria-expanded={openDropdown === 'jobs'}
                        aria-controls="jobs-menu"
                        onKeyDown={e => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            setOpenDropdown(prev => prev === 'jobs' ? null : 'jobs');
                          }
                          if (e.key === 'Escape') setOpenDropdown(null);
                        }}
                      >
                        {t('header.jobs')} <ChevronDown size={14} />
                      </Link>
                      <ul
                        id="jobs-menu"
                        className="dropdown-menu glass-panel"
                        onKeyDown={e => {
                          const items = [...e.currentTarget.querySelectorAll('a')];
                          const idx = items.indexOf(document.activeElement);
                          if (e.key === 'ArrowDown') { e.preventDefault(); items[idx + 1]?.focus(); }
                          if (e.key === 'ArrowUp')   { e.preventDefault(); items[idx - 1]?.focus(); }
                          if (e.key === 'Escape')    { setOpenDropdown(null); jobsTriggerRef.current?.focus(); }
                        }}
                      >
                        <li><Link to="/" discover="none">{t('header.allAiJobs')}</Link></li>
                        <li><Link to="/top-ai-internships" discover="none">{t('header.topAiInternships')}</Link></li>
                        <li><Link to="/ai-internships" discover="none">{t('header.aiInternships')}</Link></li>
                        <li><Link to="/entry-developer-jobs" discover="none">{t('header.developerJobs')}</Link></li>
                        <li><Link to="/entry-non-tech-jobs" discover="none">{t('header.nonTechJobs')}</Link></li>
                        <li><Link to="/entry-designer-jobs" discover="none">{t('header.designerJobs')}</Link></li>
                        <li><Link to="/?remote=true" discover="none">{t('header.remoteJobs')}</Link></li>
                      </ul>
                    </li>

                    <li
                      className={`dropdown${openDropdown === 'salaries' ? ' is-open' : ''}`}
                      onMouseEnter={() => setOpenDropdown('salaries')}
                      onMouseLeave={() => setOpenDropdown(null)}
                    >
                      <Link
                        to="/salaries"
                        className="dropdown-trigger"
                        ref={salariesTriggerRef}
                        aria-haspopup="true"
                        aria-expanded={openDropdown === 'salaries'}
                        aria-controls="salaries-menu"
                        onKeyDown={e => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            setOpenDropdown(prev => prev === 'salaries' ? null : 'salaries');
                          }
                          if (e.key === 'Escape') setOpenDropdown(null);
                        }}
                      >
                        {t('header.salaries')} <ChevronDown size={14} />
                      </Link>
                      <ul
                        id="salaries-menu"
                        className="dropdown-menu glass-panel"
                        onKeyDown={e => {
                          const items = [...e.currentTarget.querySelectorAll('a')];
                          const idx = items.indexOf(document.activeElement);
                          if (e.key === 'ArrowDown') { e.preventDefault(); items[idx + 1]?.focus(); }
                          if (e.key === 'ArrowUp')   { e.preventDefault(); items[idx - 1]?.focus(); }
                          if (e.key === 'Escape')    { setOpenDropdown(null); salariesTriggerRef.current?.focus(); }
                        }}
                      >
                        <li><Link to="/salaries" discover="none">{t('header.globalOverview')}</Link></li>
                        <li><Link to="/observatory" discover="none">{observatoryName}</Link></li>
                        <li><Link to="/developer-salaries" discover="none">{t('header.developerSalaries')}</Link></li>
                        <li><Link to="/non-tech-salaries" discover="none">{t('header.nonTechSalaries')}</Link></li>
                        <li><Link to="/highest-paying-ai-jobs" discover="none">{t('header.highestPayingAiJobs')}</Link></li>
                        <li><Link to="/usa-vs-world-salaries" discover="none">{t('header.usaVsWorldSalaries')}</Link></li>
                      </ul>
                    </li>

                    <li><Link to="/companies" discover="none">{t('header.companies')}</Link></li>
                    <li><Link to="/events" discover="none" prefetch="none">{t('header.events')}</Link></li>
                    <li><Link to="/learn-ai" discover="none">{t('header.learnAi')}</Link></li>
                    <li><Link to="/ai-tools" discover="none">{t('header.tools')}</Link></li>
                    {desktopRuntime && !PRODUCTION_PRODUCT_MODE ? <li><Link to="/desktop/workspace" discover="none">Desktop</Link></li> : null}
                  </>
                )}
              </ul>
            </nav>
          </div>

          <div className={`header-right ${prelaunchMode ? 'header-right-prelaunch' : ''}`}>
            {SHOW_PRELAUNCH_NAV_ITEMS && !waitlistMode && (
              <Link to="/post-job" className="btn btn-employeur">Employers</Link>
            )}

            <div className="header-icon-actions">
              {SHOW_PRELAUNCH_NAV_ITEMS && !waitlistMode && (
                <Link to="/candidatures" className="icon-action">
                  <Briefcase size={20} />
                  <span>Candidacies</span>
                </Link>
              )}
              {SHOW_PRELAUNCH_NAV_ITEMS && !waitlistMode && (
                <Link to="/opportunities" className="icon-action">
                  <Zap size={20} />
                  <span>Opportunities</span>
                </Link>
              )}

              <button className="icon-action theme-toggle" onClick={toggleTheme} aria-label={t('header.toggleTheme')}>
                {theme === 'dark' ? <Sun size={20} /> : <Moon size={20} />}
                <span>{theme === 'dark' ? t('header.light') : t('header.dark')}</span>
              </button>

              {!waitlistMode && (
                <>
                  <Suspense
                    fallback={(
                      <button className="icon-action" onClick={onOpenAuth} aria-label={t('header.signIn')}>
                        <User size={20} />
                        <span>{t('header.signIn')}</span>
                      </button>
                    )}
                  >
                    <HeaderAuthActions
                      variant="desktop"
                      onOpenAuth={onOpenAuth}
                      currentLanguage={currentLanguage}
                      languageOptions={LANGUAGE_OPTIONS}
                      onChangeLanguage={changeLanguage}
                      currentCurrency={currentCurrency}
                      currencyOptions={CURRENCIES}
                      onChangeCurrency={setCurrency}
                    />
                  </Suspense>
                </>
              )}
            </div>

            {/* Burger button — mobile only */}
            <button
              className="burger-btn"
              ref={burgerBtnRef}
              onClick={() => setMobileMenuOpen(true)}
              aria-label={t('header.openMenu')}
              aria-expanded={mobileMenuOpen}
              aria-controls="mobile-drawer"
            >
              <Menu size={22} />
            </button>
          </div>
        </div>
      </header>

      {/* ── Mobile drawer ─────────────────────────────────────────────────── */}
      {mobileMenuOpen && (
        <div
          className="mobile-overlay"
          onClick={close}
          role="dialog"
          aria-modal="true"
          aria-label="Navigation menu"
          onKeyDown={handleDrawerKeyDown}
        >
          <nav id="mobile-drawer" className="mobile-drawer" ref={mobileDrawerRef} onClick={e => e.stopPropagation()}>
            <div className="mobile-drawer-header">
              <Link
                to={brandTarget}
                className={`brand${observatoryBrand ? ' brand-observatory' : ''}`}
                aria-label={observatoryBrand ? `AI Careers ${observatoryName}` : undefined}
                discover="none"
                onClick={(e) => {
                  close();
                  handleBrandClick(e);
                }}
              >
                <img
                  src={logoSrc}
                  alt={observatoryBrand ? '' : 'AI Careers logo'}
                  className="brand-logo-image"
                  style={{ width: 32, height: 32 }}
                  width="32"
                  height="32"
                  decoding="async"
                />
                <span className="brand-text" style={{ fontSize: '1.1rem' }}>AI Careers{observatoryBrand && <small>{observatoryName}</small>}</span>
              </Link>
              <button className="mobile-drawer-close" onClick={close} aria-label={t('header.closeMenu')}>
                <X size={22} />
              </button>
            </div>

            <ul className="mobile-nav-list">
              {waitlistMode ? (
                <>
                  <li><Link to="/learn-ai" onClick={close} discover="none"><BookOpen size={18} /> {t('header.learnAi')}</Link></li>
                  <li><Link to="/about" onClick={close} discover="none"><Building2 size={18} /> {t('footer.aboutUs')}</Link></li>
                </>
              ) : mobileView !== 'explore' && activeMobileView ? (
                <li className="mobile-submenu-screen">
                  <button
                    type="button"
                    className="mobile-drawer-back"
                    onClick={() => setMobileView('explore')}
                    aria-label={t('mobileNav.backToExplore')}
                  >
                    <ArrowLeft size={19} />
                    {t('mobileNav.explore')}
                  </button>
                  <div className="mobile-submenu-heading">
                    <h2 id={`mobile-${mobileView}-heading`}>{activeMobileView.title}</h2>
                    <p>{activeMobileView.description}</p>
                  </div>
                  <ul id={`mobile-${mobileView}-submenu`} className="mobile-submenu-list" aria-labelledby={`mobile-${mobileView}-heading`}>
                    {activeMobileView.items.map((item, index) => (
                      <li key={item.to}>
                        <Link to={item.to} className="mobile-submenu-link" onClick={close} discover="none">
                          <span className="mobile-submenu-index">{String(index + 1).padStart(2, '0')}</span>
                          <span>{item.label}</span>
                          <ChevronRight size={19} aria-hidden="true" />
                        </Link>
                      </li>
                    ))}
                  </ul>
                </li>
              ) : (
                <>
                  <li className="mobile-explore-intro">
                    <span>{t('mobileNav.explore')}</span>
                    <p>{t('mobileNav.careerMap')}</p>
                  </li>
                  <li>
                    <div className="mobile-explore-grid">
                      <button type="button" className="mobile-explore-tile" onClick={() => openMobileView('jobs')}>
                        <span className="mobile-explore-index">01</span>
                        <Briefcase size={27} aria-hidden="true" />
                        <span>{t('header.jobs')}</span>
                      </button>
                      <button type="button" className="mobile-explore-tile" onClick={() => openMobileView('salaries')}>
                        <span className="mobile-explore-index">02</span>
                        <DollarSign size={27} aria-hidden="true" />
                        <span>{t('header.salaries')}</span>
                      </button>
                      <Link to="/companies" className="mobile-explore-tile" onClick={close} discover="none">
                        <span className="mobile-explore-index">03</span>
                        <Building2 size={27} aria-hidden="true" />
                        <span>{t('header.companies')}</span>
                      </Link>
                      {!PRODUCTION_PRODUCT_MODE ? (
                        <button type="button" className="mobile-explore-tile" onClick={() => openMobileView('products')}>
                          <span className="mobile-explore-index">04</span>
                          <Package size={27} aria-hidden="true" />
                          <span>{t('mobileNav.products')}</span>
                        </button>
                      ) : null}
                    </div>
                  </li>
                  <li className="mobile-grow-section">
                    <span className="mobile-grow-title">{t('mobileNav.grow')}</span>
                    <Link to="/learn-ai" className="mobile-grow-link" onClick={close} discover="none"><BookOpen size={20} /> <span>{t('header.learnAi')}</span> <ChevronRight size={19} aria-hidden="true" /></Link>
                    <Link to="/ai-tools" className="mobile-grow-link" onClick={close} discover="none"><Zap size={20} /> <span>{t('header.tools')}</span> <ChevronRight size={19} aria-hidden="true" /></Link>
                    <Link to="/events" className="mobile-grow-link" onClick={close} discover="none" prefetch="none"><Calendar size={20} /> <span>{t('header.events')}</span> <ChevronRight size={19} aria-hidden="true" /></Link>
                  </li>
                  {desktopRuntime && !PRODUCTION_PRODUCT_MODE ? <li><Link to="/desktop/workspace" onClick={close} discover="none"><Briefcase size={18} /> Desktop</Link></li> : null}
                </>
              )}
            </ul>

            <div className="mobile-drawer-footer">
              {mobileLanguageOpen && (
                <div id="mobile-language-menu" className="mobile-language-menu" aria-label={t('header.language')}>
                  {LANGUAGE_OPTIONS.map((option) => (
                    <button
                      key={option.code}
                      type="button"
                      className="mobile-language-option"
                      onClick={() => changeLanguage(option.code)}
                    >
                      <span>{t(option.key)}</span>
                      {currentLanguage === option.code ? <Check size={16} /> : null}
                    </button>
                  ))}
                </div>
              )}
              <div className="mobile-footer-row">
                {!waitlistMode ? (
                  <Suspense
                    fallback={(
                      <button className="mobile-signin-btn" onClick={() => { onOpenAuth(); close(); }}>
                        <User size={18} /> Sign in / Sign up
                      </button>
                    )}
                  >
                  <HeaderAuthActions variant="mobile" onOpenAuth={onOpenAuth} onClose={close} />
                  </Suspense>
                ) : (
                  <div className="mobile-signin-btn" aria-hidden="true" style={{ visibility: 'hidden' }} />
                )}
                <button className="mobile-theme-icon-btn" onClick={toggleTheme} aria-label={t('header.toggleTheme')}>
                  {theme === 'dark' ? <Sun size={20} /> : <Moon size={20} />}
                </button>
              </div>
              <button
                type="button"
                className="mobile-language-trigger"
                onClick={() => setMobileLanguageOpen((open) => !open)}
                aria-expanded={mobileLanguageOpen}
                aria-controls="mobile-language-menu"
              >
                <Globe size={19} />
                <span>{t('header.language')}</span>
                <strong>{currentLanguage.toUpperCase()}</strong>
                <ChevronDown size={17} className={mobileLanguageOpen ? 'is-open' : ''} aria-hidden="true" />
              </button>
            </div>
          </nav>
        </div>
      )}
    </>
  );
}
